// Journal "Export .md" (Jade, 2026-10-28). It used to save the note's plain
// words, exactly like .txt. Now the note's structure comes along in Markdown:
// the title, headings, bold / italic / strikethrough (underline as <u>, which
// Markdown has no mark for), bullet and numbered lists, checklists ([ ] / [x]),
// quotes, dividers, links, link pills, images, video and audio. Colours, fonts
// and the note's background can't be written in Markdown, so they're left out.
//
// Lines in the note (one <div> / <p> each) stay lines (a Markdown hard break),
// and an empty line starts a new paragraph, so the file reads like the note.

const HARD_BREAK = "  \n";

// Characters Markdown would read as formatting, written as plain text.
function escapeText(s) {
  return s.replace(/ /g, " ").replace(/([\\`*_[\]<>])/g, "\\$1");
}

// Line starts that would turn a plain line into a heading, quote, list or rule.
function escapeLineStart(line) {
  // "1. x" is escaped as "1\. x" (a backslash before a digit isn't an escape).
  const num = line.replace(/^(\s*\d+)([.)]\s)/, "$1\\$2");
  if (num !== line) return num;
  return line.replace(/^(\s*)(#{1,6}\s|>|[-+*]\s|[-*_]{3,}\s*$)/, (m, sp, mark) => sp + "\\" + mark);
}

// Moves spaces outside the marks ("** hi **" isn't bold in Markdown).
function wrap(mark, inner, close = mark) {
  if (!inner.trim()) return inner;
  const lead = inner.match(/^\s*/)[0], trail = inner.match(/\s*$/)[0];
  return lead + mark + inner.trim() + close + trail;
}

const isBold = (el) => /^(b|strong)$/i.test(el.tagName) || /^(bold|bolder|[6-9]00)$/.test(el.style?.fontWeight || "");
const isItalic = (el) => /^(i|em)$/i.test(el.tagName) || el.style?.fontStyle === "italic";
const decorations = (el) => `${el.style?.textDecoration || ""} ${el.style?.textDecorationLine || ""}`;
const isStrike = (el) => /^(s|strike|del)$/i.test(el.tagName) || decorations(el).includes("line-through");
const isUnderline = (el) => /^u$/i.test(el.tagName) || decorations(el).includes("underline");
const mediaLink = (el) => {
  const src = el.getAttribute("src");
  if (!src) return "";
  if (/^img$/i.test(el.tagName)) return `![](${src.replace(/\)/g, "%29")})`;
  return `[${/^video$/i.test(el.tagName) ? "Video" : "Audio"}](${src.replace(/\)/g, "%29")})`;
};

// One run of text with its formatting.
function inline(node, ctx = {}) {
  if (node.nodeType === 3) return escapeText(node.nodeValue).replace(/\n/g, HARD_BREAK);
  if (node.nodeType !== 1) return "";
  const el = node;
  const tag = el.tagName.toLowerCase();
  if (tag === "br") return HARD_BREAK;
  if (tag === "img" || tag === "video" || tag === "audio") return mediaLink(el);
  if (tag === "style" || tag === "script" || el.classList?.contains("jrnl-check-box")) return "";
  const next = { ...ctx };
  const bold = !ctx.b && isBold(el), italic = !ctx.i && isItalic(el);
  const strike = !ctx.s && isStrike(el), under = !ctx.u && isUnderline(el);
  if (bold) next.b = true;
  if (italic) next.i = true;
  if (strike) next.s = true;
  if (under) next.u = true;
  let out = Array.from(el.childNodes).map(n => inline(n, next)).join("");
  if (tag === "a" && el.getAttribute("href")) {
    const href = el.getAttribute("href").replace(/\)/g, "%29");
    return `[${out.trim() || href}](${href})`;
  }
  if (under) out = wrap("<u>", out, "</u>");
  if (strike) out = wrap("~~", out);
  if (italic) out = wrap("*", out);
  if (bold) out = wrap("**", out);
  return out;
}

const BLOCK_TAGS = new Set(["div", "p", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "blockquote", "hr", "pre", "section", "article", "figure", "video", "audio"]);
const isBlock = (n) => n.nodeType === 1 && (BLOCK_TAGS.has(n.tagName.toLowerCase()) || n.classList?.contains("jrnl-check"));

function checkLine(row) {
  const box = row.querySelector(".jrnl-check-box");
  const label = row.querySelector(".jrnl-check-label") || row;
  const done = box?.getAttribute("data-checked") === "1";
  // A ticked item's label is struck through on screen; [x] already says so.
  const text = Array.from(label.childNodes).map(n => inline(n, { s: done })).join("").replace(/( {2}\n)+$/, "").trim();
  return `- [${done ? "x" : " "}] ${text}`;
}

function listLines(list, depth) {
  const ordered = list.tagName.toLowerCase() === "ol";
  const pad = "   ".repeat(depth);
  const lines = [];
  let n = 1;
  Array.from(list.children).forEach(child => {
    const tag = child.tagName.toLowerCase();
    if (tag === "ul" || tag === "ol") { lines.push(...listLines(child, depth + 1)); return; }
    if (tag !== "li") return;
    const nested = [];
    const text = Array.from(child.childNodes).map(c => {
      if (c.nodeType === 1 && /^(ul|ol)$/i.test(c.tagName)) { nested.push(...listLines(c, depth + 1)); return ""; }
      return isBlock(c) ? Array.from(c.childNodes).map(x => inline(x)).join("") : inline(c);
    }).join("").replace(/( {2}\n)+$/, "").trim();
    lines.push(`${pad}${ordered ? `${n++}.` : "-"} ${text.replace(/ {2}\n/g, `  \n${pad}   `)}`);
    lines.push(...nested);
  });
  return lines;
}

// Walks a container (the note body, a quote, a nested div) into blocks.
// Each block is { kind, text }; consecutive "check" blocks join as one list.
function blocksOf(container) {
  const blocks = [];
  let para = [];   // lines of the paragraph being built
  let pending = ""; // inline content that isn't inside a line element yet
  const flush = () => {
    if (para.length) blocks.push({ kind: "p", text: para.join(HARD_BREAK) });
    para = [];
  };
  // One line of the note; an empty one ends the paragraph.
  const pushLine = (text) => {
    const t = text.replace(/( {2}\n)+$/, "");
    if (t.trim()) para.push(t.split("\n").map(escapeLineStart).join("\n"));
    else flush();
  };
  const endPending = () => { if (pending) { pushLine(pending); pending = ""; } };
  Array.from(container.childNodes).forEach(node => {
    if (!isBlock(node)) {
      if (node.nodeType === 1 && node.tagName.toLowerCase() === "br") { pushLine(pending); pending = ""; return; }
      pending += inline(node);
      return;
    }
    const el = node;
    const tag = el.tagName.toLowerCase();
    endPending();
    if (el.classList.contains("jrnl-check")) { flush(); blocks.push({ kind: "check", text: checkLine(el) }); return; }
    if (/^h[1-6]$/.test(tag)) {
      flush();
      const text = Array.from(el.childNodes).map(n => inline(n, { b: true })).join("").replace(/ *\n/g, " ").trim();
      if (text) blocks.push({ kind: "h", text: `${"#".repeat(Number(tag[1]))} ${text}` });
      return;
    }
    if (tag === "ul" || tag === "ol") { flush(); blocks.push({ kind: "list", text: listLines(el, 0).join("\n") }); return; }
    if (tag === "hr") { flush(); blocks.push({ kind: "hr", text: "---" }); return; }
    if (tag === "video" || tag === "audio") { flush(); blocks.push({ kind: "p", text: mediaLink(el) }); return; }
    if (tag === "blockquote") {
      flush();
      const inner = joinBlocks(blocksOf(el));
      if (inner.trim()) blocks.push({ kind: "quote", text: inner.split("\n").map(l => (l ? `> ${l}` : ">")).join("\n") });
      return;
    }
    // A line (<div> / <p>). One with blocks inside is a container.
    if (Array.from(el.childNodes).some(isBlock)) {
      flush();
      blocks.push(...blocksOf(el));
      return;
    }
    pushLine(Array.from(el.childNodes).map(n => inline(n)).join(""));
  });
  endPending();
  flush();
  return blocks;
}

function joinBlocks(blocks) {
  let out = "";
  blocks.forEach((b, i) => {
    if (i > 0) out += b.kind === "check" && blocks[i - 1].kind === "check" ? "\n" : "\n\n";
    out += b.text;
  });
  return out;
}

// The whole note as Markdown. `html` is the note body's HTML; an empty title
// writes no heading.
export function noteToMarkdown({ title = "", html = "" } = {}) {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const body = joinBlocks(blocksOf(doc.body));
  const head = title.trim() ? `# ${escapeText(title.trim())}` : "";
  return [head, body].filter(Boolean).join("\n\n") + "\n";
}
